import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Package, Store, TriangleAlert, BookOpen, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Link } from "react-router-dom";
import LeaderboardCard from "@/components/LeaderboardCard";

function StatCard({ label, value, icon: Icon, accent, to }) {
  const content = (
    <div className="grid-border-card p-6 cursor-pointer h-full" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-start justify-between mb-6">
        <div className="overline">{label}</div>
        <Icon className={`w-5 h-5 ${accent ? "text-accent" : "text-muted-foreground"}`} strokeWidth={1.5} />
      </div>
      <div className="font-display text-4xl tracking-tight">{value ?? "—"}</div>
      {to && <div className="text-xs uppercase tracking-widest text-muted-foreground mt-4">View →</div>}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      const url = user.role === "admin" ? "/dashboard/admin" : `/dashboard/store/${user.store_id}`;
      const { data } = await api.get(url);
      setData(data);
    };
    load();
  }, [user]);

  if (user.role === "admin") {
    return (
      <div className="space-y-10" data-testid="admin-dashboard">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Overview</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Network at a glance</h1>
          </div>
          <div className="text-sm text-muted-foreground">All stores · live data</div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Stores" value={data?.total_stores} icon={Store} to="/stores" />
          <StatCard label="Total SKUs" value={data?.total_products} icon={BookOpen} to="/products" />
          <StatCard label="Units in Stock" value={data?.total_units?.toLocaleString()} icon={Package} to="/inventory" />
          <StatCard label="Low Stock Alerts" value={data?.low_stock_count} icon={TriangleAlert} accent to="/inventory?filter=low" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="grid-border-card lg:col-span-2 overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="overline">Inventory by store</div>
                <h2 className="font-display text-xl mt-1">Bookstore footprint</h2>
              </div>
              <Link to="/stores" className="text-xs uppercase tracking-widest text-accent hover:underline">Manage</Link>
            </div>
            <div className="divide-y divide-border">
              {(data?.by_store || []).map((s) => {
                const max = Math.max(...(data.by_store.map(x => x.units) || [1]), 1);
                const pct = (s.units / max) * 100;
                return (
                  <div key={s.store_id} className="px-6 py-3 grid grid-cols-12 items-center gap-4">
                    <div className="col-span-5 text-sm font-medium truncate">{s.store_name}</div>
                    <div className="col-span-5 h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="col-span-2 text-right font-mono text-sm">{s.units}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid-border-card overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <div className="overline">Activity</div>
              <h2 className="font-display text-xl mt-1">Recent movements</h2>
            </div>
            <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
              {(data?.recent_activity || []).map((t) => (
                <div key={t.id} className="px-6 py-3 flex items-center gap-3">
                  {t.quantity_change > 0
                    ? <ArrowUpRight className="w-4 h-4 text-success shrink-0" strokeWidth={1.5}/>
                    : <ArrowDownRight className="w-4 h-4 text-accent shrink-0" strokeWidth={1.5}/>}
                  <div className="flex-1 text-sm min-w-0">
                    <div className="truncate font-medium">{t.product_name || t.action_type}</div>
                    <div className="text-xs text-muted-foreground truncate flex items-center gap-1.5">
                      <Store className="w-3 h-3 shrink-0" strokeWidth={1.5}/>
                      <span className="truncate">{t.store_name || "—"}</span>
                      <span>·</span>
                      <span className="truncate">{t.action_type}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                  <div className="font-mono text-sm">{t.quantity_change > 0 ? "+" : ""}{t.quantity_change}</div>
                </div>
              ))}
              {(!data?.recent_activity || data.recent_activity.length === 0) && (
                <div className="px-6 py-8 text-sm text-muted-foreground text-center">No activity yet</div>
              )}
            </div>
          </div>
        </div>

        <LeaderboardCard />
      </div>
    );
  }

  // Store user dashboard
  return (
    <div className="space-y-10" data-testid="store-dashboard">
      <div>
        <div className="overline">My store</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Today's shelf</h1>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <StatCard label="Total Units" value={data?.total_units?.toLocaleString()} icon={Package} to="/inventory" />
        <StatCard label="Low Stock" value={data?.low_stock?.length} icon={TriangleAlert} accent to="/inventory?filter=low" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="grid-border-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <div className="overline">Last received</div>
            <h2 className="font-display text-xl mt-1">Recent inbound</h2>
          </div>
          <div className="divide-y divide-border">
            {(data?.recent_in || []).map((t) => (
              <div key={t.id} className="px-6 py-3 flex items-center justify-between">
                <div className="text-sm min-w-0 flex-1">
                  <div className="truncate font-medium">{t.product_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{t.action_type} · {new Date(t.created_at).toLocaleString()}</div>
                </div>
                <div className="font-mono text-success">+{t.quantity_change}</div>
              </div>
            ))}
            {(!data?.recent_in || data.recent_in.length===0) && <div className="px-6 py-8 text-sm text-muted-foreground text-center">No recent inbound</div>}
          </div>
        </div>

        <div className="grid-border-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <div className="overline">Last reduced</div>
            <h2 className="font-display text-xl mt-1">Recent outbound</h2>
          </div>
          <div className="divide-y divide-border">
            {(data?.recent_out || []).map((t) => (
              <div key={t.id} className="px-6 py-3 flex items-center justify-between">
                <div className="text-sm min-w-0 flex-1">
                  <div className="truncate font-medium">{t.product_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{t.action_type} · {new Date(t.created_at).toLocaleString()}</div>
                </div>
                <div className="font-mono text-accent">{t.quantity_change}</div>
              </div>
            ))}
            {(!data?.recent_out || data.recent_out.length===0) && <div className="px-6 py-8 text-sm text-muted-foreground text-center">No recent outbound</div>}
          </div>
        </div>
      </div>

      {data?.low_stock?.length > 0 && (
        <div className="grid-border-card overflow-hidden border-accent/40">
          <div className="px-6 py-4 border-b border-border bg-accent/5">
            <div className="overline text-accent">Action needed</div>
            <h2 className="font-display text-xl mt-1">Low stock items</h2>
          </div>
          <div className="divide-y divide-border">
            {data.low_stock.map((i) => (
              <div key={i.id} className="px-6 py-3 flex items-center justify-between">
                <div className="text-sm">{i.product_name}</div>
                <div className="font-mono text-sm text-accent">{i.quantity} left</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <LeaderboardCard />
    </div>
  );
}
